import { useFormContext } from "@validereinc/common-components";
import { type FormSchemaQuestionDisplayType } from "@validereinc/domain";
import { useState } from "react";

export const useFormTemplateQuestionDrawer = (
  formFieldSectionQuestionsName: string,
  displayQuestions: FormSchemaQuestionDisplayType[]
) => {
  const [isAddQuestionDrawerOpen, setIsAddQuestionDrawerOpen] = useState(false);
  const [questionIdToDelete, setQuestionIdToDelete] = useState<string>("");
  const [questionToEdit, setQuestionToEdit] =
    useState<FormSchemaQuestionDisplayType | null>(null);

  const parentForm = useFormContext();
  const sectionQuestionIds = parentForm.watch(formFieldSectionQuestionsName);
  const questionPreview = parentForm.watch("question_preview");
  const submissionDeduplicationKeys = parentForm.watch(
    "submission_deduplication_keys"
  );
  const questions = parentForm.watch("config.questions");

  const deleteQuestionFromSectionQuestionIds = (questionIdToDelete: string) => {
    let newSectionQuestionIds = [...sectionQuestionIds];
    newSectionQuestionIds = newSectionQuestionIds.filter(
      (questionId) => questionId !== questionIdToDelete
    );
    parentForm.setValue(formFieldSectionQuestionsName, [
      ...newSectionQuestionIds,
    ]);
  };

  const deleteQuestionFromConfigQuestionIds = (questionIdToDelete: string) => {
    let newQuestions = {
      ...questions,
    } as Record<string, FormSchemaQuestionDisplayType>;

    newQuestions = Object.fromEntries(
      Object.entries(newQuestions).map(([questionId, question]) => {
        const questionNumberToDelete = displayQuestions?.find(
          (displayQuestion) =>
            displayQuestion.question_id === questionIdToDelete
        )?.question_number;

        const currentQuestionNumber =
          question?.question_number ??
          displayQuestions.filter((displayQuestion) => {
            return displayQuestion.question_id === questionId;
          })[0]?.question_number;

        const newQuestionNumber =
          questionNumberToDelete &&
          currentQuestionNumber > questionNumberToDelete
            ? currentQuestionNumber - 1
            : currentQuestionNumber;

        return [
          questionId,
          { ...question, question_number: newQuestionNumber },
        ];
      })
    );

    delete newQuestions[questionIdToDelete];
    parentForm.setValue("config.questions", { ...newQuestions });
  };

  const handleDeleteQuestion = (questionIdToDelete: string) => {
    deleteQuestionFromSectionQuestionIds(questionIdToDelete);
    deleteQuestionFromConfigQuestionIds(questionIdToDelete);
    parentForm.setValue(
      "question_preview",
      questionPreview.filter(
        (preview) => preview.question_id !== questionIdToDelete
      )
    );
    parentForm.setValue(
      "submission_deduplication_keys",
      submissionDeduplicationKeys.filter(
        (submissionDeduplicationKey) =>
          submissionDeduplicationKey.question_id !== questionIdToDelete
      )
    );
    onCloseAddQuestionDrawer();
  };

  const onCloseAddQuestionDrawer = () => {
    setQuestionToEdit(null);
    setIsAddQuestionDrawerOpen(false);
  };

  const onOpenAddQuestionDrawer = () => {
    setIsAddQuestionDrawerOpen(true);
  };

  const onSaveQuestionDrawer = (newQuestionId: string) => {
    parentForm.setValue(
      formFieldSectionQuestionsName,
      sectionQuestionIds.concat(newQuestionId)
    );
  };

  return {
    isAddQuestionDrawerOpen,
    questionIdToDelete,
    questionToEdit,
    setQuestionIdToDelete,
    setQuestionToEdit,
    handleDeleteQuestion,
    onCloseAddQuestionDrawer,
    onOpenAddQuestionDrawer,
    onSaveQuestionDrawer,
  };
};
